import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { motion } from 'framer-motion';
import { TimelineDataPointDto } from '../../types/campaigns.types';
import GlassmorphismTheme from '../../styles/glassmorphism';

interface TimelineChartProps {
  data: TimelineDataPointDto[];
  title?: string;
  usaReceita?: boolean;
}

/** Parse YYYY-MM-DD (ou ISO) sem deslocamento de fuso para ordenação cronológica. */
function parseTimelineDate(iso: string): number {
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    return Date.UTC(year, month - 1, day);
  }
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function formatTimelineLabel(ts: number): string {
  return new Date(ts).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'UTC',
  });
}

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const TimelineChart: React.FC<TimelineChartProps> = ({ data, title, usaReceita = false }) => {
  const chartData = [...data]
    .map((point) => ({
      sortKey: parseTimelineDate(point.data),
      'Receita do Dia': point.receitaDia ?? 0,
      'Receita Acumulada': point.receitaAcumulada ?? 0,
      'Linhas do Dia': point.linhasAditivasValidadas,
      Acumulado: point.linhasAditivasAcumuladas,
      'Progresso %': Number((point.progressoDia * 100).toFixed(1)),
    }))
    .sort((a, b) => a.sortKey - b.sortKey);

  const diaKey = usaReceita ? 'Receita do Dia' : 'Linhas do Dia';
  const acumuladoKey = usaReceita ? 'Receita Acumulada' : 'Acumulado';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      style={{
        ...GlassmorphismTheme.components.glassChartCard,
        width: '100%',
        height: '100%'
      }}
    >
      {title && (
        <h3 style={{
          fontSize: '1.1rem',
          fontWeight: 600,
          marginBottom: GlassmorphismTheme.spacing.md,
          color: GlassmorphismTheme.colors.solid.text,
          paddingBottom: GlassmorphismTheme.spacing.sm,
          borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
        }}>
          {title}
        </h3>
      )}
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.3)" />
          <XAxis 
            dataKey="sortKey"
            type="number"
            domain={['dataMin', 'dataMax']}
            tickFormatter={formatTimelineLabel}
            stroke={GlassmorphismTheme.colors.solid.textLight}
            style={{ fontSize: '12px' }}
          />
          <YAxis
            yAxisId="left"
            stroke={GlassmorphismTheme.colors.solid.textLight}
            tickFormatter={usaReceita ? (v) => formatCurrency(Number(v)) : undefined}
            style={{ fontSize: '12px' }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            domain={[0, 100]}
            stroke="#f39c12"
            tickFormatter={(v) => `${v}%`}
            style={{ fontSize: '12px' }}
          />
          <Tooltip 
            labelFormatter={(ts) => formatTimelineLabel(Number(ts))}
            formatter={(value: number, name: string) => {
              if (usaReceita && (name === 'Receita do Dia' || name === 'Receita Acumulada')) {
                return [formatCurrency(value), name];
              }
              if (name === 'Progresso %') {
                return [`${value}%`, name];
              }
              return [value, name];
            }}
            contentStyle={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.95)', 
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(176, 196, 222, 0.3)',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(31, 38, 135, 0.15)',
              color: GlassmorphismTheme.colors.solid.text
            }}
          />
          <Legend 
            wrapperStyle={{ paddingTop: '20px', color: GlassmorphismTheme.colors.solid.text }}
          />
          <Line 
            yAxisId="left"
            type="monotone" 
            dataKey={diaKey}
            stroke={usaReceita ? '#2185d0' : '#E6E6FA'}
            strokeWidth={2}
            dot={{ fill: usaReceita ? '#2185d0' : '#E6E6FA', r: 4 }}
            activeDot={{ r: 6 }}
          />
          <Line 
            yAxisId="left"
            type="monotone" 
            dataKey={acumuladoKey}
            stroke="#2ecc71" 
            strokeWidth={2}
            dot={{ fill: '#2ecc71', r: 4 }}
            activeDot={{ r: 6 }}
          />
          <Line 
            yAxisId="right"
            type="monotone" 
            dataKey="Progresso %" 
            stroke="#f39c12" 
            strokeWidth={2}
            dot={{ fill: '#f39c12', r: 4 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </motion.div>
  );
};
